import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/apiClient.js";
import { SessionContext, type SessionState, type SessionStatus } from "../lib/session.js";

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<SessionState["user"]>(null);
  const decidedRef = useRef(false);

  useEffect(() => {
    let active = true;
    api
      .me()
      .then((res) => {
        if (!active || decidedRef.current) return;
        setUser(res.user);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!active || decidedRef.current) return;
        setUser(null);
        setStatus("anonymous");
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    decidedRef.current = true;
    setUser(res.user);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    decidedRef.current = true;
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({ status, user, login, logout }),
    [status, user, login, logout]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
